import React, {useState} from "react";
import {
    Alert,
    Button,
    Form,
    FormAlert,
    Progress,
    ProgressSize,
    ProgressMeasureLocation,
    ProgressVariant
} from "@patternfly/react-core";
import {
    TimesIcon,
    UserIcon,
    EnvelopeIcon,
    LockIcon,
    EyeIcon,
    EyeSlashIcon,
    BoltIcon,
    CheckIcon
} from "@patternfly/react-icons";
import {createAccount, resendConfirmationEmail} from "@app/utils/utils";
import {BorderedInput} from "@app/Login/BorderedInput";
import {useWindowSize, isMobile} from "@app/utils/useWindowSize";

const PURPLE_GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)';

export interface ICreateAccountComponentProps {
    isModalOpen: boolean;
    onModalClose: any;
    setEmailAddress: Function;
}

const CreateAccountComponent: React.FunctionComponent<ICreateAccountComponentProps> = (props) => {

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [emailAddress, setEmailAddress] = useState('');
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('');
    const [validated, setValidated] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [resendSent, setResendSent] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { width } = useWindowSize();
    const mobile = isMobile(width);

    const getPasswordStrength = (pwd: string): { strength: number; label: string; variant: ProgressVariant; color: string } => {
        if (!pwd) return { strength: 0, label: '', variant: ProgressVariant.danger, color: '#dc2626' };

        let strength = 0;
        if (pwd.length >= 8) strength += 25;
        if (pwd.length >= 12) strength += 15;
        if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 20;
        if (/\d/.test(pwd)) strength += 20;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength += 20;

        if (strength < 40) return { strength, label: 'Weak', variant: ProgressVariant.danger, color: '#dc2626' };
        if (strength < 70) return { strength, label: 'Fair', variant: ProgressVariant.warning, color: '#f59e0b' };
        return { strength, label: 'Strong', variant: ProgressVariant.success, color: '#059669' };
    };

    const passwordStrength = getPasswordStrength(password);
    const passwordsMatch = password && confirmPassword && password === confirmPassword;

    const resetForm = () => {
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        setEmailAddress('');
        setValidated('');
        setErrorMessage('');
        setRegistrationComplete(false);
        setResendSent(false);
        setIsResending(false);
        setShowPassword(false);
        setShowConfirmPassword(false);
    }

    const handleResendEmail = async () => {
        setIsResending(true);
        try {
            await resendConfirmationEmail(emailAddress);
            setResendSent(true);
        } catch (error) {
            // silently fail
        } finally {
            setIsResending(false);
        }
    }

    const handleBackToLogin = () => {
        props.setEmailAddress(emailAddress);
        resetForm();
        props.onModalClose();
    }

    const doCreateAccount = async () => {
        if (!username.trim()) {
            setValidated("error");
            setErrorMessage("Please enter a display name");
            return;
        }

        if (!emailAddress.trim()) {
            setValidated("error");
            setErrorMessage("Please enter an email address");
            return;
        }

        if (password !== confirmPassword) {
            setValidated("error");
            setErrorMessage("Passwords don't match");
            return;
        }

        if (passwordStrength.strength < 40) {
            setValidated("error");
            setErrorMessage("Please choose a stronger password");
            return;
        }

        setIsLoading(true);
        setValidated('');

        const payload = {
            "displayName": username,
            "email": emailAddress,
            "password": password
        };

        try {
            await createAccount(payload);
            setRegistrationComplete(true);
        } catch (error: any) {
            setValidated("error");
            if (error?.status === 403 && error?.data?.reason === 'signup_not_open') {
                setErrorMessage(
                    <>
                        Sorry — Advolt is currently in closed beta and signups are limited. Reach out to the{' '}
                        <a href="https://get.advolt.app" target="_blank" rel="noopener noreferrer">team</a>
                        {' '}if you'd like access.
                    </>
                );
            } else {
                setErrorMessage("Failed to create account. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    }

    const closeAndResetModal = () => {
        resetForm();
        props.onModalClose();
    }

    const isFormValid = username && emailAddress && password && confirmPassword && passwordsMatch && passwordStrength.strength >= 40;

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: mobile ? '0' : '24px'
        },
        modal: {
            background: '#ffffff',
            borderRadius: mobile ? '0' : '20px',
            width: '100%',
            maxWidth: mobile ? 'none' : '480px',
            maxHeight: mobile ? '100%' : '92vh',
            height: mobile ? '100%' : 'auto',
            overflow: 'auto',
            boxShadow: mobile ? 'none' : '0 24px 64px rgba(15, 23, 42, 0.25)',
            position: 'relative' as const
        },
        closeButton: {
            position: 'absolute' as const,
            top: '16px',
            right: '16px',
            background: '#f3f4f6',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            transition: 'background 0.15s',
            zIndex: 2
        },
        body: {
            padding: mobile ? '40px 20px 24px' : '40px 40px 32px'
        },
        logoBadge: {
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)'
        },
        title: {
            color: '#0f172a',
            fontSize: '28px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '8px',
            textAlign: 'center' as const,
            letterSpacing: '-0.5px'
        },
        subtitle: {
            color: '#6b7280',
            fontSize: '15px',
            margin: 0,
            marginBottom: '28px',
            textAlign: 'center' as const
        },
        fieldLabel: {
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '8px'
        },
        fieldGroup: {
            marginBottom: '16px'
        },
        strengthContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '10px'
        },
        strengthLabel: {
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px'
        },
        actionsContainer: {
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '12px'
        },
        submitButton: {
            background: PURPLE_GRADIENT,
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)'
        },
        submitButtonDisabled: {
            background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: 'none',
            cursor: 'not-allowed'
        },
        linkButton: {
            color: '#6366f1',
            fontWeight: 600,
            textAlign: 'center' as const,
            fontSize: '14px'
        },
        formReset: {
            border: 'none',
            padding: 0,
            background: 'transparent'
        } as React.CSSProperties,
        eyeButton: {
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            padding: 0,
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center'
        } as React.CSSProperties,
        successIconWrap: {
            width: '72px',
            height: '72px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            color: '#059669',
            fontSize: '32px'
        }
    };

    if (!props.isModalOpen) {
        return null;
    }

    if (registrationComplete) {
        return (
            <div style={styles.overlay} onClick={closeAndResetModal}>
                <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <button
                        style={styles.closeButton}
                        onClick={closeAndResetModal}
                        aria-label="Close"
                        type="button"
                    >
                        <TimesIcon />
                    </button>

                    <div style={styles.body}>
                        <div style={styles.successIconWrap}>
                            <CheckIcon />
                        </div>
                        <h1 style={styles.title}>Check your email</h1>
                        <p style={styles.subtitle}>One more step to get started</p>

                        <div style={{ textAlign: 'center' }}>
                            <p style={{ fontSize: '15px', color: '#4b5563', marginBottom: '6px' }}>
                                We sent a confirmation link to
                            </p>
                            <p style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a', marginBottom: '20px' }}>
                                {emailAddress}
                            </p>
                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: 1.6 }}>
                                Please check your inbox and click the link to verify your account before logging in.
                            </p>

                            {resendSent && (
                                <Alert
                                    variant="success"
                                    title="Confirmation email resent!"
                                    aria-live="polite"
                                    isInline
                                    style={{ marginBottom: '16px', textAlign: 'left' }}
                                />
                            )}

                            <div style={styles.actionsContainer}>
                                <Button
                                    variant="primary"
                                    onClick={handleResendEmail}
                                    isLoading={isResending}
                                    isDisabled={isResending}
                                    isBlock
                                    style={isResending ? styles.submitButtonDisabled : styles.submitButton}
                                >
                                    {isResending ? 'Resending...' : 'Resend email'}
                                </Button>
                                <Button
                                    variant="link"
                                    onClick={handleBackToLogin}
                                    style={styles.linkButton}
                                    isBlock
                                >
                                    Back to login
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.overlay} onClick={closeAndResetModal}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <button
                    style={styles.closeButton}
                    onClick={closeAndResetModal}
                    aria-label="Close"
                    type="button"
                >
                    <TimesIcon />
                </button>

                <div style={styles.body}>
                    <div style={styles.logoBadge}>
                        <BoltIcon style={{ fontSize: '28px', color: '#fbbf24' }} />
                    </div>
                    <h1 style={styles.title}>Create your account</h1>
                    <p style={styles.subtitle}>Join us today and get started in minutes</p>

                    <Form style={styles.formReset}>
                        {validated === 'error' && (
                            <FormAlert>
                                <Alert
                                    variant="danger"
                                    title={errorMessage}
                                    aria-live="polite"
                                    isInline
                                />
                            </FormAlert>
                        )}

                        <div style={styles.fieldGroup}>
                            <label style={styles.fieldLabel} htmlFor="username-id">Display Name</label>
                            <BorderedInput
                                id="username-id"
                                value={username}
                                onChange={setUsername}
                                placeholder="Your name"
                                icon={<UserIcon />}
                                autoComplete="name"
                            />
                        </div>

                        <div style={styles.fieldGroup}>
                            <label style={styles.fieldLabel} htmlFor="email-id">Email</label>
                            <BorderedInput
                                id="email-id"
                                value={emailAddress}
                                onChange={setEmailAddress}
                                type="email"
                                placeholder="you@example.com"
                                icon={<EnvelopeIcon />}
                                autoComplete="email"
                            />
                        </div>

                        <div style={styles.fieldGroup}>
                            <label style={styles.fieldLabel} htmlFor="password-id">Password</label>
                            <BorderedInput
                                id="password-id"
                                value={password}
                                onChange={setPassword}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Create a strong password"
                                icon={<LockIcon />}
                                autoComplete="new-password"
                                rightAdornment={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((s) => !s)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        style={styles.eyeButton}
                                    >
                                        {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                    </button>
                                }
                            />
                            {password && (
                                <div style={styles.strengthContainer}>
                                    <div style={{ flex: 1 }}>
                                        <Progress
                                            value={passwordStrength.strength}
                                            size={ProgressSize.sm}
                                            measureLocation={ProgressMeasureLocation.none}
                                            variant={passwordStrength.variant}
                                            aria-label="Password strength"
                                        />
                                    </div>
                                    <span style={{ ...styles.strengthLabel, color: passwordStrength.color }}>
                                        {passwordStrength.label}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div style={styles.fieldGroup}>
                            <label style={styles.fieldLabel} htmlFor="confirm-password-id">Confirm Password</label>
                            <BorderedInput
                                id="confirm-password-id"
                                value={confirmPassword}
                                onChange={setConfirmPassword}
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Re-enter your password"
                                icon={<LockIcon />}
                                autoComplete="new-password"
                                hasError={!!(confirmPassword && !passwordsMatch)}
                                rightAdornment={
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword((s) => !s)}
                                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                        style={styles.eyeButton}
                                    >
                                        {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                    </button>
                                }
                            />
                            {confirmPassword && !passwordsMatch && (
                                <div style={{ color: '#dc2626', fontSize: '13px', marginTop: '6px' }}>
                                    Passwords do not match
                                </div>
                            )}
                        </div>

                        <div style={styles.actionsContainer}>
                            <Button
                                variant="primary"
                                onClick={doCreateAccount}
                                isLoading={isLoading}
                                isDisabled={!isFormValid || isLoading}
                                isBlock
                                style={!isFormValid || isLoading ? styles.submitButtonDisabled : styles.submitButton}
                            >
                                {isLoading ? 'Creating account...' : 'Create account'}
                            </Button>
                            <Button
                                variant="link"
                                onClick={closeAndResetModal}
                                style={styles.linkButton}
                                isBlock
                            >
                                Already have an account? Sign in
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
}

export {CreateAccountComponent}
